import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import LogoUploader, { resolveLogoUrl } from "@/components/LogoUploader";
import { toast } from "sonner";
import { Plus, X, Buildings, Trash, PencilSimple, Users as UsersIcon, Upload } from "@phosphor-icons/react";
import ImportModal from "@/components/ImportModal";

export default function SuperAdmin() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = () => api.get("/companies").then(r => setCompanies(r.data));
  useEffect(() => { reload(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta empresa y todos sus usuarios?")) return;
    try { await api.delete(`/companies/${id}`); toast.success("Empresa eliminada"); reload(); }
    catch { toast.error("Error"); }
  };

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Panel Super Admin</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Empresas</h1>
          <p className="text-neutral-500 mt-2">Administra todos los tenants del sistema.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={()=>setImportOpen(true)} className="inline-flex items-center gap-2 px-4 py-3 bg-white border border-neutral-300 hover:border-neutral-400 text-neutral-800 font-semibold rounded-md" data-testid="import-companies-btn">
            <Upload size={18} weight="bold"/> Importar empresas
          </button>
          <button onClick={()=>{ setEditing(null); setOpen(true); }} className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md" data-testid="new-company-btn">
            <Plus size={18} weight="bold"/> Nueva empresa
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.map(c => (
          <div key={c.id} className="bg-white border border-neutral-200 rounded-md p-5 hover:-translate-y-0.5 transition-all duration-200" data-testid={`company-card-${c.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-md bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center overflow-hidden">
                {c.logo_url ? <img src={resolveLogoUrl(c.logo_url)} className="w-full h-full object-contain bg-white" alt=""/> : <Buildings size={22} weight="duotone"/>}
              </div>
              <div className="flex gap-1">
                <button onClick={()=>{ setEditing(c); setOpen(true); }} className="p-1.5 text-neutral-400 hover:text-blue-600" data-testid={`edit-company-${c.id}`}><PencilSimple size={16}/></button>
                <button onClick={()=>onDelete(c.id)} className="p-1.5 text-neutral-400 hover:text-rose-500" data-testid={`delete-company-${c.id}`}><Trash size={16}/></button>
              </div>
            </div>
            <h3 className="font-display text-lg font-bold tracking-tight">{c.name}</h3>
            <p className="text-sm text-neutral-500 mb-3">{c.tax_id || "—"}</p>
            <div className="space-y-1 text-xs text-neutral-600">
              {c.email && <div>📧 {c.email}</div>}
              {c.phone && <div>📞 {c.phone}</div>}
              {c.address && <div className="truncate">📍 {c.address}</div>}
            </div>
          </div>
        ))}
        {companies.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 py-16 text-center text-neutral-400 border border-dashed border-neutral-300 rounded-md">
            <Buildings size={48} className="mx-auto text-neutral-300 mb-4" weight="duotone"/>
            <p>Aún no hay empresas registradas. Crea la primera.</p>
          </div>
        )}
      </div>

      {open && <CompanyForm initial={editing} onClose={()=>setOpen(false)} onSuccess={()=>{ setOpen(false); reload(); }}/>}
      {importOpen && (
        <ImportModal
          title="Importar empresas"
          templateUrl="/companies/import/template"
          templateFilename="plantilla_empresas.csv"
          previewUrl="/companies/import/preview"
          importUrl="/companies/import"
          columnsHint="name, tax_id, email, phone, address, logo_url, description"
          columns={[
            { key: "name", label: "Nombre" },
            { key: "tax_id", label: "RFC" },
            { key: "email", label: "Email" },
          ]}
          onClose={()=>setImportOpen(false)}
          onSuccess={()=>{ setImportOpen(false); reload(); }}
          testidPrefix="import-companies"
        />
      )}
    </div>
  );
}

function CompanyForm({ initial, onClose, onSuccess }) {
  const [f, setF] = useState(initial || { name:"", address:"", phone:"", email:"", tax_id:"", logo_url:"", description:"" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.name) { toast.error("Nombre requerido"); return; }
    setBusy(true);
    try {
      if (initial?.id) await api.put(`/companies/${initial.id}`, f);
      else await api.post("/companies", f);
      toast.success(initial ? "Empresa actualizada" : "Empresa creada");
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="company-form-modal">
      <div className="bg-white rounded-md w-full max-w-xl border border-neutral-200 anim-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-xl font-bold tracking-tight">{initial ? "Editar empresa" : "Nueva empresa"}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X size={20}/></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-3">
          {[["name","Nombre"],["tax_id","RFC / Tax ID"],["email","Email"],["phone","Teléfono"]].map(([k,l]) => (
            <div key={k}>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">{l}</label>
              <input value={f[k]||""} onChange={(e)=>setF({...f,[k]:e.target.value})}
                className="w-full px-3 py-2.5 border border-neutral-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                data-testid={`company-form-${k}`}/>
            </div>
          ))}
          {initial?.id && (
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Logo</label>
              <LogoUploader companyId={initial.id} url={f.logo_url} canEdit onChange={(logo_url) => setF(p => ({ ...p, logo_url }))}/>
            </div>
          )}
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Dirección</label>
            <input value={f.address||""} onChange={(e)=>setF({...f,address:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="company-form-address"/>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Descripción</label>
            <textarea value={f.description||""} onChange={(e)=>setF({...f,description:e.target.value})} rows={2} className="w-full px-3 py-2 border border-neutral-200 rounded-md" data-testid="company-form-description"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="company-form-submit">
            {busy ? "Guardando..." : (initial ? "Guardar" : "Crear")}
          </button>
        </div>
      </div>
    </div>
  );
}
